import { cn } from '@tempo-io/sui-class-utils';
import { parseISO } from 'date-fns';
import {
  DateFormat,
  formatDayMonth,
  formatLocalDate,
  fromDateString,
} from '@/lib/utils/common/dateFormats';

interface TooltipScheduleBarProps {
  forecastedStart: string;
  forecastedFinish: string;
  latestFinish?: string;
  isAtRisk: boolean;
}

export const TooltipScheduleBar = ({
  forecastedStart,
  forecastedFinish,
  latestFinish,
  isAtRisk,
}: TooltipScheduleBarProps) => {
  const startDate = parseISO(forecastedStart);
  const finishDate = parseISO(forecastedFinish);
  const latestDate = latestFinish ? parseISO(latestFinish) : finishDate;

  const totalDuration = latestDate.getTime() - startDate.getTime();
  const forecastedDuration = finishDate.getTime() - startDate.getTime();
  const forecastedWidthPercent =
    totalDuration > 0
      ? Math.min(100, Math.max(0, (forecastedDuration / totalDuration) * 100))
      : forecastedDuration > 0
        ? 100
        : 0;
  const uncertaintyWidthPercent = 100 - forecastedWidthPercent;

  const formatDate = (dateStr: string) => {
    const localDate = fromDateString(formatLocalDate(dateStr, DateFormat.ISO_DATE));
    return localDate ? formatDayMonth(localDate) : '';
  };

  const hasUncertaintySection = latestFinish && uncertaintyWidthPercent > 0;
  const hasSolidSection = forecastedWidthPercent > 0;

  return (
    <div className="relative h-7 @container/tooltip-bar">
      <div className="absolute inset-0 flex items-center">
        {hasSolidSection && (
          <div
            className={cn(
              'absolute h-full bg-bg-neutral-bold flex items-center @container/solid',
              hasUncertaintySection ? 'rounded-l-lg' : 'rounded-lg',
            )}
            style={{
              left: '0%',
              width: `${forecastedWidthPercent}%`,
            }}
          >
            <span className="absolute left-2 text-xs font-bold text-text-neutral-inverted hidden @[60px]/solid:inline">
              {formatDate(forecastedStart)}
            </span>
            <span className="absolute right-2 text-xs font-bold text-text-neutral-inverted hidden @[120px]/solid:inline">
              {formatDate(forecastedFinish)}
            </span>
          </div>
        )}
        {hasUncertaintySection && (
          <div
            className={cn(
              'absolute h-full border border-dashed border-icon-brand-secondary bg-bg-neutral flex items-center @container/dashed',
              hasSolidSection ? 'rounded-r-lg' : 'rounded-lg',
            )}
            style={{
              left: `${forecastedWidthPercent}%`,
              width: `${uncertaintyWidthPercent}%`,
            }}
          >
            <span
              className={cn(
                'absolute right-2 text-xs font-bold hidden @[40px]/dashed:inline',
                isAtRisk ? 'text-text-error' : 'text-text-brand',
              )}
            >
              {formatDate(latestFinish)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
